import { useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { hydrateNotifications, setLoading, setError } from '../store/slices/notificationsSlice';
import { notificationService } from '../services/api';

// Polling configuration
const POLLING_CONFIG = {
  INITIAL_INTERVAL: 60000,    // 60 seconds
  MAX_INTERVAL: 300000,       // 5 minutes
  NO_NEW_MULTIPLIER: 1.5,     // Increase interval when no new notifications
  ERROR_RETRY_INTERVAL: 30000, // 30 seconds on error
  MAX_CONSECUTIVE_ERRORS: 3    // Stop polling after consecutive errors
};

const useNotificationPolling = () => {
  const dispatch = useDispatch();
  const { 
    connectionStatus, 
    pollingEnabled, 
    lastFetch,
    notifications 
  } = useSelector(state => state.notifications);
  
  // Refs for managing polling state
  const pollingIntervalRef = useRef(null);
  const currentIntervalRef = useRef(POLLING_CONFIG.INITIAL_INTERVAL);
  const consecutiveErrorsRef = useRef(0);
  const lastNotificationCountRef = useRef(0);
  const mountedRef = useRef(true);

  // Fetch notifications from API
  const fetchNotifications = useCallback(async () => {
    if (!mountedRef.current) return;

    try {
      dispatch(setLoading(true));
      dispatch(setError(null));
      
      const response = await notificationService.getUnreadNotifications();
      
      if (response.success) {
        const newNotifications = response.data;

        // Merge into the store (dedupe by id, stamps lastFetch, never toasts).
        // The poll fetches UNREAD rows only, so it must go through the hydrate
        // merge — a replace would wipe the read history from badge + dropdown.
        // The server unread total rides along so the badge stays correct
        // beyond the page cap while the socket is down.
        dispatch(
          hydrateNotifications({
            list: newNotifications,
            unreadCount: response.pagination?.unread_count,
          })
        );

        // Reset consecutive errors on success
        consecutiveErrorsRef.current = 0;
        
        // Adjust polling interval based on activity
        adjustPollingInterval(newNotifications.length);
        
        console.log(`📡 Polling fetched ${newNotifications.length} notifications`);
      } else {
        throw new Error(response.error);
      }
    } catch (error) {
      console.error('Notification polling error:', error);
      consecutiveErrorsRef.current++;
      
      dispatch(setError(error.message));
      
      // Stop polling after too many consecutive errors
      if (consecutiveErrorsRef.current >= POLLING_CONFIG.MAX_CONSECUTIVE_ERRORS) {
        console.error('Too many consecutive polling errors. Stopping polling.');
        stopPolling();
        return;
      }
      
      // Use error retry interval
      currentIntervalRef.current = POLLING_CONFIG.ERROR_RETRY_INTERVAL;
    } finally {
      if (mountedRef.current) {
        dispatch(setLoading(false));
      }
    }
  }, [dispatch]);

  // Adjust polling interval based on notification activity
  const adjustPollingInterval = useCallback((currentNotificationCount) => {
    const hasNewNotifications = currentNotificationCount > lastNotificationCountRef.current;
    lastNotificationCountRef.current = currentNotificationCount;
    
    if (hasNewNotifications) {
      // Reset to initial interval if there are new notifications
      currentIntervalRef.current = POLLING_CONFIG.INITIAL_INTERVAL;
    } else {
      // Gradually increase interval if no new notifications
      currentIntervalRef.current = Math.min(
        currentIntervalRef.current * POLLING_CONFIG.NO_NEW_MULTIPLIER,
        POLLING_CONFIG.MAX_INTERVAL
      );
    }
    
    console.log(`📊 Adjusted polling interval to ${currentIntervalRef.current / 1000}s`);
  }, []);

  // Start polling
  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    
    console.log('📡 Starting notification polling');
    
    // Fetch immediately
    fetchNotifications();
    
    // Set up recurring polling
    pollingIntervalRef.current = setInterval(() => {
      fetchNotifications();
    }, currentIntervalRef.current);
  }, [fetchNotifications]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      console.log('📡 Stopping notification polling');
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  // Restart polling with fresh interval
  const restartPolling = useCallback(() => {
    currentIntervalRef.current = POLLING_CONFIG.INITIAL_INTERVAL;
    consecutiveErrorsRef.current = 0;
    stopPolling();
    setTimeout(startPolling, 1000); // Small delay before restart
  }, [startPolling, stopPolling]);

  // Handle polling state changes
  useEffect(() => {
    const shouldPoll = pollingEnabled && 
                     (connectionStatus === 'disconnected' || connectionStatus === 'error');
    
    if (shouldPoll) {
      startPolling();
    } else {
      stopPolling();
    }
    
    return () => {
      stopPolling();
    };
  }, [pollingEnabled, connectionStatus, startPolling, stopPolling]);

  // Handle visibility change (pause/resume polling when tab is hidden/visible)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Pause polling when tab is hidden
        stopPolling();
      } else if (pollingEnabled && 
                (connectionStatus === 'disconnected' || connectionStatus === 'error')) {
        // Resume polling when tab becomes visible
        restartPolling();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [pollingEnabled, connectionStatus, restartPolling, stopPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      stopPolling();
    };
  }, [stopPolling]);

  return {
    isPolling: !!pollingIntervalRef.current,
    currentInterval: currentIntervalRef.current,
    consecutiveErrors: consecutiveErrorsRef.current,
    lastFetch,
    startPolling,
    stopPolling,
    restartPolling,
    fetchNotifications: () => {
      // Manual fetch without affecting polling interval
      fetchNotifications();
    }
  };
};

export default useNotificationPolling; 